import { KeenSliderInstance, TrackDetails } from 'keen-slider/react';
import ErrorReporter from '#/lib/ErrorReporter';
import { addDays, isSameDay } from 'date-fns';
import { makeAutoObservable, reaction } from 'mobx';
import { ALTERNATE_DATES_DAYS_TO_LOAD, alternateDatesStore, AvailabilitySearchResult } from '../../../../store/AlternateDatesStore';
import { setTime } from '#/lib/dates';

export default class AlternateDatesSliderStore {
    currentSlide = 0;
    currentIndex = 0;
    previousIndex = 0;
    loaded = false;
    slider?: KeenSliderInstance;
    slideDetails: TrackDetails['slides'] = [];
    loadedChunkIndices: Set<number> = new Set();
    isAnimating = false;
    firstLoaded = false;

    constructor() {
        makeAutoObservable(this, {
            slider: false,
        });
        this.currentIndex = this.initialIndex;
        reaction(
            () => this.currentSlide,
            (current, prev) => {
                this.previousIndex = prev || this.initialIndex;
                this.currentIndex = current;
                const direction = this.currentIndex > this.previousIndex ? 'next' : 'prev';
                const lookahead = 3;

                const upcomingIndex = direction === 'next' ? current + lookahead : current - lookahead;

                const chunkSize = ALTERNATE_DATES_DAYS_TO_LOAD + 1;
                const chunkIndex = Math.floor(upcomingIndex / chunkSize);

                if (chunkIndex <= 0) {
                    if (!this.loadedChunkIndices.has(0)) {
                        this.loadedChunkIndices.add(0);
                        alternateDatesStore.loadPage(addDays(new Date(), ALTERNATE_DATES_DAYS_TO_LOAD / 2)).catch(ErrorReporter.captureError);
                    }
                    return;
                }

                const middleIndexOfChunk = chunkIndex * chunkSize + Math.floor(chunkSize / 2) + 1;
                const targetSlide = this.slides[middleIndexOfChunk];
                const shouldLoad = targetSlide?.placeholder || !targetSlide?.fleetCategories?.length;
                const dateToLoad = targetSlide?.pickUpDate;
                if (dateToLoad && shouldLoad && !this.loadedChunkIndices.has(chunkIndex)) {
                    this.loadedChunkIndices.add(chunkIndex);
                    alternateDatesStore.loadPage(dateToLoad).catch(ErrorReporter.captureError);
                }
            }
        );
    }

    get dates() {
        if (!alternateDatesStore.trip.pickUpDate) {
            return [];
        }
        const start = new Date();
        const end = addDays(alternateDatesStore.trip.pickUpDate, 365);
        const dates = [];
        let currentDate = start;
        while (currentDate <= end) {
            dates.push(setTime(currentDate, alternateDatesStore.trip.pickUpDate));
            currentDate = addDays(currentDate, 1);
        }
        return dates;
    }

    get slides(): AvailabilitySearchResult[] {
        return this.dates.map((date) => {
            const availability = alternateDatesStore.results.find((r) => isSameDay(r.pickUpDate, date));
            return availability
                ? availability
                : {
                      ...alternateDatesStore.results[0],
                      fleetCategories: [],
                      placeholder: true,
                      pickUpDate: date,
                      dropOffDate: addDays(date, alternateDatesStore.hireDays),
                  };
        });
    }

    getAvailabilityByIdx(index: number) {
        const slideIndex = this.slideDetails?.[index]?.abs;
        return slideIndex !== undefined ? this.slides[slideIndex] : undefined;
    }

    get initialIndex() {
        return this.slides.findIndex((s) => isSameDay(s.pickUpDate, alternateDatesStore.trip.pickUpDate as Date));
    }

    get ready() {
        return Boolean(this.slider && this.loaded && this.slideDetails);
    }
}
